import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components, ExtraProps } from 'react-markdown';

type MarkdownProps = {
  children: string;
  className?: string;
  enhanceChildren?: (children: React.ReactNode) => React.ReactNode;
};

const mergeClassName = (base?: string, extra?: string) => {
  if (base && extra) return `${base} ${extra}`;
  if (base) return base;
  if (extra) return extra;
  return undefined;
};

type MarkdownCodeProps = JSX.IntrinsicElements['code'] &
  ExtraProps & { inline?: boolean };

const MarkdownBase: React.FC<MarkdownProps> = ({
  children,
  className,
  enhanceChildren,
}) => {
  const components: Components = {
    h1: ({ node: _node, className: headingClassName, ...props }) => (
      <h1
        {...props}
        className={mergeClassName(
          'first:mt-0 mt-6 mb-3 text-2xl font-semibold text-gray-900',
          headingClassName
        )}
      />
    ),
    h2: ({ node: _node, className: headingClassName, ...props }) => (
      <h2
        {...props}
        className={mergeClassName(
          'first:mt-0 mt-5 mb-2 text-xl font-semibold text-gray-900',
          headingClassName
        )}
      />
    ),
    h3: ({ node: _node, className: headingClassName, ...props }) => (
      <h3
        {...props}
        className={mergeClassName(
          'first:mt-0 mt-4 mb-2 text-lg font-semibold text-gray-900',
          headingClassName
        )}
      />
    ),
    h4: ({ node: _node, className: headingClassName, ...props }) => (
      <h4
        {...props}
        className={mergeClassName(
          'first:mt-0 mt-4 mb-2 text-base font-semibold text-gray-900',
          headingClassName
        )}
      />
    ),
    p: ({
      node: _node,
      className: paragraphClassName,
      children: pChildren,
      ...props
    }) => (
      <p
        {...props}
        className={mergeClassName(
          'my-2 leading-7 text-gray-800 whitespace-pre-line',
          paragraphClassName
        )}
      >
        {enhanceChildren ? enhanceChildren(pChildren) : pChildren}
      </p>
    ),
    ul: ({ node: _node, className: listClassName, ...props }) => (
      <ul
        {...props}
        className={mergeClassName(
          'my-2 list-disc space-y-1 pl-5 text-gray-800',
          listClassName
        )}
      />
    ),
    ol: ({ node: _node, className: listClassName, ...props }) => (
      <ol
        {...props}
        className={mergeClassName(
          'my-2 list-decimal space-y-1 pl-5 text-gray-800',
          listClassName
        )}
      />
    ),
    li: ({
      node: _node,
      className: listItemClassName,
      children: liChildren,
      ...props
    }) => (
      <li
        {...props}
        className={mergeClassName('leading-6 text-gray-800', listItemClassName)}
      >
        {enhanceChildren ? enhanceChildren(liChildren) : liChildren}
      </li>
    ),
    blockquote: ({
      node: _node,
      className: blockquoteClassName,
      children: bqChildren,
      ...props
    }) => (
      <blockquote
        {...props}
        className={mergeClassName(
          'border-l-4 border-gray-300 pl-4 italic text-gray-700',
          blockquoteClassName
        )}
      >
        {enhanceChildren ? enhanceChildren(bqChildren) : bqChildren}
      </blockquote>
    ),
    a: ({ node: _node, className: linkClassName, ...props }) => (
      <a
        {...props}
        className={mergeClassName(
          'text-blue-600 underline hover:text-blue-700',
          linkClassName
        )}
        target="_blank"
        rel="noopener noreferrer"
      />
    ),
    code: (props) => {
      const {
        inline,
        className: codeClassName,
        children: codeChildren,
        node: _node,
        ...rest
      } = props as MarkdownCodeProps;

      return inline ? (
        <code
          {...rest}
          className={mergeClassName(
            'rounded bg-gray-100 px-1 py-[0.125rem] text-sm text-gray-800',
            codeClassName
          )}
        >
          {codeChildren}
        </code>
      ) : (
        <pre className="overflow-x-auto rounded-md bg-gray-900 p-3 text-sm text-gray-100">
          <code {...rest} className={mergeClassName(undefined, codeClassName)}>
            {codeChildren}
          </code>
        </pre>
      );
    },
    details: ({ node: _node, className: detailsClassName, ...props }) => (
      <details
        {...props}
        className={mergeClassName(
          'my-3 overflow-hidden rounded-md border border-gray-200 bg-white',
          detailsClassName
        )}
      />
    ),
    summary: ({ node: _node, className: summaryClassName, ...props }) => (
      <summary
        {...props}
        className={mergeClassName(
          'cursor-pointer select-none bg-gray-100 px-3 py-2 font-medium text-gray-900 hover:bg-gray-200',
          summaryClassName
        )}
      />
    ),
  };

  return (
    <div className={mergeClassName('space-y-4', className)}>
      <ReactMarkdown components={components}>{children}</ReactMarkdown>
    </div>
  );
};

// Avoid re-rendering heavy markdown when props haven't changed
export const Markdown = React.memo(
  MarkdownBase,
  (prev, next) =>
    prev.children === next.children &&
    prev.className === next.className &&
    prev.enhanceChildren === next.enhanceChildren
);
